import React, { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import api from "../../../../api/api";
import QUESTIONNAIRES from "./questionnaireDefs";
import { getPracticeId } from "../../../../config/practice";

// Native questionnaire flow (migrated from cloud /bangquestions, 2026-09-02).
// Layout follows the practice's "Refined Editorial Healthcare" mock
// (newui.html): a split navy-rail hub where the patient sees all selected
// forms as a checklist, opens one at a time, and returns to the hub — each
// form SUBMITS ON COMPLETION (no data loss if the session is abandoned).
// Patient View (fullscreen + staff PIN) hides every score; staff mode shows
// the per-form result card the mock designs. Answer payloads keep the legacy
// qanswer_{form}_{n} contract so viewers/history are untouched.

const FORM_BADGES = {
    1: { t: "SB", c: "#0E7490" },
    2: { t: "MM", c: "#4F46E5" },
    3: { t: "AD", c: "#B45309" },
    4: { t: "P9", c: "#1D4ED8" },
    5: { t: "MD", c: "#7C3AED" },
    6: { t: "HS", c: "#50869A" },
    7: { t: "P2", c: "#0369A1" },
};

const FormBadge = ({ formId, size = 40, round = false }) => {
    const b = FORM_BADGES[formId] || { t: "Q", c: "#6B7280" };
    return (
        <span className="inline-flex items-center justify-center font-bold shrink-0"
            style={{
                width: size, height: size,
                borderRadius: round ? "50%" : Math.round(size * 0.28),
                background: `${b.c}14`, color: b.c,
                fontSize: Math.round(size * 0.32), letterSpacing: "0.02em",
                border: `1px solid ${b.c}30`,
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            }}>
            {b.t}
        </span>
    );
};

// Decorative strip for the standalone Forms station's empty state.
export const FormBadgeStrip = () => (
    <div className="mt-8">
        <div className="text-[11px] font-bold tracking-[0.08em] uppercase text-[#8A93A6] mb-3 font-font1">Available questionnaires</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {QUESTIONNAIRES.map((q) => (
                <div key={q.formId} className="flex items-center gap-3 bg-white rounded-xl border border-[#E5E9F0] px-3 py-2.5">
                    <FormBadge formId={q.formId} size={32} />
                    <span className="text-[13px] text-[#374151] font-font1">{q.shortName}</span>
                </div>
            ))}
        </div>
    </div>
);

// Severity tone for result cards, derived from the interpretation text.
const toneFor = (interp) => {
    const s = (interp || "").toLowerCase();
    if (/severe|high risk|positive screen|consistent with adult adhd|self-harm|review required/.test(s))
        return { color: "#991b1b", bg: "#fee2e2", border: "#f3b4b4" };
    if (/moderate|intermediate|mild|excessive/.test(s))
        return { color: "#92400e", bg: "#fef3c7", border: "#ecd9a0" };
    return { color: "#065f46", bg: "#ecfdf5", border: "#b7e2d0" };
};

const CSS = `
.qfx * { box-sizing: border-box; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
.qfx {
  --navy:#0b1f3a; --navy2:#173454; --cream:#f3f0e9; --paper:#fff;
  --gold:#c7a45d; --goldsoft:#efe4cb; --ink:#13243d; --ink2:#526176;
  --ink3:#7b8798; --line:#e6e1d8; --surface:#faf9f6; --success:#23785a;
}
.qfx-wrap {
  width:100%; max-width:1180px; margin:0 auto; overflow:hidden;
  border:1px solid rgba(11,31,58,.08); border-radius:28px; background:var(--paper);
  box-shadow:0 30px 80px rgba(11,31,58,.22);
  display:flex; flex-direction:column; max-height:94vh; position:relative;
}
.qfx-split { display:grid; grid-template-columns:minmax(300px,36%) minmax(0,1fr); flex:1; min-height:0; }
.qfx-rail {
  background:var(--navy); color:#fff; padding:44px 40px 36px;
  display:flex; flex-direction:column; align-items:flex-start; min-height:0;
}
.qfx-eyebrow {
  display:inline-flex; align-items:center; gap:10px; margin-bottom:40px;
  color:#f5ead2; font-size:.72rem; font-weight:600; letter-spacing:.16em; text-transform:uppercase;
}
.qfx-eyebrow-mark {
  width:34px; height:34px; display:grid; place-items:center;
  border:1px solid rgba(239,228,203,.55); border-radius:10px; color:var(--gold);
  font-size:.68rem; letter-spacing:.06em;
}
.qfx-rail h1 { margin:0 0 16px; font-size:clamp(1.9rem,3vw,2.7rem); font-weight:500; letter-spacing:-.045em; line-height:1.05; color:#fff; }
.qfx-rail-sub { margin:0; color:rgba(255,255,255,.7); font-size:.95rem; line-height:1.65; max-width:320px; }
.qfx-rail-bottom { margin-top:auto; width:100%; padding-top:28px; }
.qfx-progress-track { width:100%; height:6px; border-radius:999px; background:rgba(255,255,255,.13); overflow:hidden; }
.qfx-progress-fill { height:100%; border-radius:inherit; background:var(--gold); transition:width .5s cubic-bezier(.4,0,.2,1); }
.qfx-progress-label { margin-top:13px; color:rgba(255,255,255,.72); font-size:.82rem; letter-spacing:.04em; }
.qfx-back-pill {
  display:inline-flex; align-items:center; gap:8px; height:42px; padding:0 16px;
  border:1px solid rgba(255,255,255,.25); border-radius:999px; background:transparent;
  color:rgba(255,255,255,.9); font-size:.85rem; font-weight:500; cursor:pointer; transition:all .2s;
}
.qfx-back-pill:hover { border-color:var(--gold); color:var(--goldsoft); }
.qfx-count-big { display:flex; align-items:baseline; gap:6px; font-size:2rem; font-weight:500; letter-spacing:-.04em; color:#fff; }
.qfx-count-big .dim { color:rgba(255,255,255,.42); }
.qfx-count-sub { display:block; margin-top:5px; color:rgba(255,255,255,.62); font-size:.77rem; letter-spacing:.04em; }
.qfx-panel { background:var(--surface); padding:44px 46px 40px; overflow-y:auto; min-height:0; }
.qfx-list-heading { display:flex; align-items:flex-end; justify-content:space-between; gap:20px; margin-bottom:22px; }
.qfx-list-heading h2 { margin:0; color:var(--navy); font-size:1.4rem; font-weight:600; letter-spacing:-.025em; }
.qfx-list-heading p { margin:5px 0 0; color:var(--ink2); font-size:.9rem; }
.qfx-time { flex:0 0 auto; padding:8px 13px; border:1px solid var(--line); border-radius:999px; background:#fff; color:var(--ink2); font-size:.78rem; font-weight:600; }
.qfx-card {
  width:100%; display:flex; align-items:center; gap:18px; text-align:left;
  min-height:96px; padding:19px 21px; margin-bottom:13px; cursor:pointer;
  border:1px solid var(--line); border-radius:18px; background:var(--paper);
  box-shadow:0 1px 2px rgba(11,31,58,.03); transition:all .22s ease; appearance:none;
}
.qfx-card:hover:not(.done) { border-color:rgba(11,31,58,.38); transform:translateY(-2px); box-shadow:0 14px 30px rgba(11,31,58,.09); }
.qfx-card.done { border-color:rgba(35,120,90,.22); background:#f5faf7; cursor:default; }
.qfx-card-title { margin:0 0 4px; color:var(--navy); font-size:1.03rem; font-weight:650; letter-spacing:-.015em; }
.qfx-card-sub { margin:0; color:var(--ink2); font-size:.85rem; line-height:1.45; }
.qfx-badge { flex:0 0 auto; padding:7px 12px; border-radius:999px; font-size:.66rem; font-weight:700; letter-spacing:.07em; text-transform:uppercase; }
.qfx-badge.pending { background:var(--cream); color:var(--ink2); }
.qfx-badge.done { background:#e1f1e9; color:var(--success); }
.qfx-arrow {
  width:34px; height:34px; display:grid; place-items:center; flex:0 0 auto;
  border-radius:50%; background:var(--surface); color:var(--navy); font-size:1rem; transition:all .2s;
}
.qfx-card:hover:not(.done) .qfx-arrow { transform:translateX(3px); background:var(--navy); color:#fff; }
.qfx-done-icon { width:52px; height:52px; border-radius:15px; display:grid; place-items:center; background:var(--success); color:#fff; font-size:1.1rem; font-weight:700; flex-shrink:0; }
.qfx-privacy { display:flex; align-items:center; gap:9px; margin-top:22px; color:var(--ink3); font-size:.8rem; line-height:1.5; }
.qfx-privacy-mark { width:20px; height:20px; display:grid; place-items:center; flex:0 0 auto; border:1px solid var(--line); border-radius:50%; color:var(--success); font-size:.68rem; }
.qfx-alldone { margin-top:24px; padding:30px 32px; border:1px solid rgba(35,120,90,.22); border-radius:18px; background:#f5faf7; }
.qfx-alldone h2 { margin:0 0 8px; color:var(--navy); font-size:1.5rem; font-weight:600; letter-spacing:-.02em; }
.qfx-alldone p { margin:0; color:var(--ink2); font-size:.93rem; line-height:1.6; }
.qfx-instructions { margin:0 0 26px; padding:0 0 0 18px; border-left:3px solid var(--gold); color:var(--ink2); font-size:.95rem; line-height:1.65; }
.qfx-qcard {
  margin-bottom:13px; padding:25px 26px 26px; border:1px solid var(--line);
  border-radius:18px; background:#fff; box-shadow:0 1px 2px rgba(11,31,58,.025);
  transition:border-color .2s, box-shadow .2s;
}
.qfx-qcard.answered { border-color:rgba(35,120,90,.25); box-shadow:0 5px 18px rgba(35,120,90,.045); }
.qfx-qcard.missing { border-color:#e3a69f; box-shadow:0 5px 18px rgba(217,88,80,.08); }
.qfx-qnum { margin-bottom:9px; color:var(--ink3); font-size:.68rem; font-weight:700; letter-spacing:.11em; text-transform:uppercase; }
.qfx-qtext { margin-bottom:19px; color:var(--navy); font-size:1.05rem; font-weight:500; line-height:1.55; }
.qfx-opts { display:flex; flex-wrap:wrap; gap:9px; }
.qfx-opt {
  display:inline-flex; align-items:center; min-height:46px; padding:10px 17px;
  border:1px solid var(--line); border-radius:12px; background:var(--surface);
  color:var(--ink2); font-size:.88rem; font-weight:500; cursor:pointer;
  transition:all .18s; user-select:none; -webkit-tap-highlight-color:transparent; appearance:none;
}
.qfx-opt:hover { border-color:rgba(11,31,58,.45); background:#fff; color:var(--navy); }
.qfx-opt.on { border-color:var(--navy); background:var(--navy); color:#fff; font-weight:600; box-shadow:0 5px 14px rgba(11,31,58,.16); }
.qfx-opt.on::before { content:"✓"; margin-right:7px; color:var(--gold); }
.qfx-validation { margin:18px 0 0; padding:14px 16px; border-radius:12px; background:#fff1ef; color:#993b35; font-size:.88rem; line-height:1.45; }
.qfx-submit-row {
  position:sticky; bottom:0; z-index:3; margin-top:22px; padding:18px 20px;
  border:1px solid var(--line); border-radius:18px; background:rgba(255,255,255,.95);
  box-shadow:0 14px 40px rgba(11,31,58,.11); display:flex; align-items:center;
  justify-content:space-between; gap:16px; flex-wrap:wrap; backdrop-filter:blur(4px);
}
.qfx-note { color:var(--ink3); font-size:.78rem; }
.qfx-btn {
  min-height:50px; padding:13px 26px; border:none; border-radius:12px;
  background:var(--navy); color:#fff; font-size:.85rem; font-weight:600;
  letter-spacing:.03em; cursor:pointer; transition:all .2s; -webkit-tap-highlight-color:transparent;
}
.qfx-btn:hover:not(:disabled) { background:var(--navy2); box-shadow:0 10px 24px rgba(11,31,58,.18); transform:translateY(-1px); }
.qfx-btn:disabled { background:#cfd6df; cursor:not-allowed; }
.qfx-btn.ghost { background:transparent; border:1px solid var(--line); color:var(--ink); }
.qfx-btn.ghost:hover { border-color:var(--navy); box-shadow:none; background:#fff; }
.qfx-result { margin-top:20px; padding:28px 30px; border-radius:18px; border:1px solid; }
.qfx-result h3 { margin:0 0 8px; font-size:1.4rem; font-weight:600; letter-spacing:-.02em; }
.qfx-result p { margin:0; font-size:.95rem; line-height:1.6; }
.qfx-x {
  position:absolute; top:16px; right:16px; z-index:5;
  width:38px; height:38px; border-radius:50%; border:none; cursor:pointer;
  background:rgba(255,255,255,.92); color:var(--ink); font-size:15px;
  display:grid; place-items:center; box-shadow:0 2px 10px rgba(11,31,58,.18);
}
.qfx-vitals { display:grid; grid-template-columns:repeat(5,1fr); gap:10px; margin-top:14px; }
.qfx-vitals input { width:100%; border:1px solid var(--line); border-radius:10px; padding:9px 10px; font-size:.9rem; background:#fff; }
.qfx-vitals label { display:block; font-size:.66rem; font-weight:700; letter-spacing:.08em; color:var(--ink3); margin-bottom:5px; text-transform:uppercase; }
@media (max-width:900px) {
  .qfx-split { grid-template-columns:1fr; }
  .qfx-rail { min-height:0; padding:28px 26px 24px; }
  .qfx-eyebrow { margin-bottom:22px; }
  .qfx-rail-bottom { padding-top:20px; }
  .qfx-panel { padding:28px 22px 32px; }
  .qfx-wrap { border-radius:20px; }
}
@media (max-width:620px) {
  .qfx-opts { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); }
  .qfx-opt { justify-content:center; text-align:center; padding:10px 9px; }
  .qfx-badge { display:none; }
  .qfx-vitals { grid-template-columns:repeat(3,1fr); }
}
`;

// forcePatientView: the standalone Forms station always hands off to the
// patient — Patient View is implied, no toggle shown.
const QuestionnaireFill = ({ open, onClose, data, forcePatientView = false }) => {
    const practiseid = getPracticeId();
    const userConfig = useSelector((state) => state.userconfig);
    const [selected, setSelected] = useState([]);
    const [step, setStep] = useState("select");   // select | hub | form | results
    const [activeFormId, setActiveFormId] = useState(null);
    const [answers, setAnswers] = useState({});   // {formId: {key: value}}
    const [vitals, setVitals] = useState({ ptheight: "", ptweight: "", ptbmi: "", ptbp: "", ptnc: "" });
    const [submitted, setSubmitted] = useState({}); // {formId: {scored, ok}}
    const [submitting, setSubmitting] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [formResult, setFormResult] = useState(null); // staff-mode per-form result card
    // Patient View machinery (fullscreen, PIN, hidden scores).
    const [patientView, setPatientView] = useState(false);
    const [pin, setPin] = useState("");
    const [pinEntry, setPinEntry] = useState("");
    const [pinStage, setPinStage] = useState(null); // null | 'setup' | 'unlock'
    const [pinError, setPinError] = useState("");
    const [locked, setLocked] = useState(false);
    const [unlockMode, setUnlockMode] = useState("pin"); // 'pin' | 'creds'
    const [credUser, setCredUser] = useState("");
    const [credPass, setCredPass] = useState("");
    const [credBusy, setCredBusy] = useState(false);
    const containerRef = useRef(null);
    const panelRef = useRef(null);

    const enterFullscreen = () => {
        try { containerRef.current?.requestFullscreen?.().catch(() => {}); } catch (_) {}
    };
    const exitFullscreen = () => {
        try { if (document.fullscreenElement) document.exitFullscreen?.(); } catch (_) {}
    };
    const unlock = () => {
        setLocked(false); setPinStage(null); setPinEntry(""); setPinError("");
        setUnlockMode("pin"); setCredUser(""); setCredPass(""); setCredBusy(false);
        exitFullscreen();
    };

    const credUnlock = async () => {
        if (!credUser || !credPass || credBusy) return;
        setCredBusy(true); setPinError("");
        try {
            const params = new URLSearchParams();
            params.append("username", credUser);
            params.append("password", credPass);
            params.append("client_id", practiseid);
            const resp = await api.post("/auth/token", params, {
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
            });
            if (resp?.access_token) unlock();
            else { setPinError("Invalid username or password"); setCredPass(""); }
        } catch (_) {
            setPinError("Invalid username or password"); setCredPass("");
        } finally { setCredBusy(false); }
    };

    const patientName = `${data?.firstname || ""} ${data?.lastname || ""}`.trim();
    const providerName = (data?.linked_provider || "").trim() || data?.providername || "";

    const activeDefs = useMemo(
        () => selected.map((id) => QUESTIONNAIRES.find((q) => q.formId === id)).filter(Boolean),
        [selected]
    );
    const def = activeDefs.find((d) => d.formId === activeFormId) || null;
    const formAnswers = (def && answers[def.formId]) || {};
    const doneCount = activeDefs.filter((d) => submitted[d.formId]).length;
    const allDone = activeDefs.length > 0 && doneCount === activeDefs.length;
    const totalMinutes = activeDefs.reduce((s, d) => s + (d.minutes || 3), 0);

    const setAnswer = (key, value) => {
        setShowValidation(false);
        setAnswers((prev) => ({
            ...prev,
            [def.formId]: { ...(prev[def.formId] || {}), [key]: value },
        }));
    };

    const requiredKeys = def ? def.items.filter((i) => i.required !== false).map((i) => i.key) : [];
    const answeredCount = requiredKeys.filter((k) => formAnswers[k] !== undefined && formAnswers[k] !== "").length;
    const formComplete = def && answeredCount === requiredKeys.length;

    const reset = () => {
        setSelected([]); setStep("select"); setActiveFormId(null); setAnswers({});
        setSubmitted({}); setSubmitting(false); setShowValidation(false); setFormResult(null);
        setVitals({ ptheight: "", ptweight: "", ptbmi: "", ptbp: "", ptnc: "" });
        setPatientView(false); setPin(""); setPinEntry(""); setPinStage(null);
        setPinError(""); setLocked(false); setUnlockMode("pin"); setCredUser(""); setCredPass("");
        exitFullscreen();
    };

    const buildJson = (d) => {
        const a = answers[d.formId] || {};
        const scored = d.score(a);
        const cdate = dayjs().format("MM-DD-YYYY");
        const json = {
            form_name: d.formName,
            form_id: String(d.formId),
            patient_id: String(data?.patientid || ""),
            patient_name: patientName,
            provider_name: providerName,
            cdate,
            dob: data?.dob || "",
            source: "care",
            score_summary: `${scored.total}/${scored.max} — ${scored.interpretation}`,
            ...(d.formId === 5 ? { cdate_5: cdate } : {}),
            ...(d.hasVitals ? vitals : {}),
            ...scored.extra,
            ...a,
        };
        return { json, scored };
    };

    // Per-form submit on completion — nothing is lost if the session ends early.
    const submitForm = async (d) => {
        if (!formComplete || submitting) {
            setShowValidation(true);
            const firstMissing = panelRef.current?.querySelector?.(".qfx-qcard.missing, .qfx-qcard:not(.answered)");
            firstMissing?.scrollIntoView?.({ behavior: "smooth", block: "center" });
            return;
        }
        setSubmitting(true);
        const { json, scored } = buildJson(d);
        let ok = false;
        try {
            const res = await api.post(
                `/v1/${practiseid}/patient/${data?.patientid}/forms/insert-db`,
                {
                    form_name: d.formName,
                    form_id: d.formId,
                    json_data: json,
                    created_by: userConfig?.username ||
                        `${userConfig?.firstname || ""} ${userConfig?.lastname || ""}`.trim() || "care",
                }
            );
            ok = res?.status === 200;
        } catch (_) { ok = false; }
        setSubmitted((prev) => ({ ...prev, [d.formId]: { scored, ok } }));
        setSubmitting(false);
        if (locked) {
            // Patient View: never show scores — straight back to the hub.
            setActiveFormId(null); setStep("hub");
        } else {
            setFormResult({ def: d, scored, ok });
        }
    };

    const keypadPress = (dgt) => {
        setPinError("");
        if (dgt === "⌫") setPinEntry((p) => p.slice(0, -1));
        else setPinEntry((p) => (p + dgt).slice(0, 4));
    };
    const confirmPin = () => {
        if (pinEntry.length !== 4) return;
        if (pinStage === "setup") {
            setPin(pinEntry); setPinEntry(""); setPinStage(null);
            setLocked(true); enterFullscreen();
            setStep("hub");
        } else if (pinEntry === pin) {
            unlock();
        } else {
            setPinError("Incorrect PIN — try again"); setPinEntry("");
        }
    };

    if (!open) return null;

    const effectivePV = forcePatientView || patientView;

    const railProgress = activeDefs.length ? Math.round((doneCount / activeDefs.length) * 100) : 0;

    return (
        <div ref={containerRef} className="qfx fixed inset-0 z-[9500] flex items-center justify-center p-3 sm:p-6"
            style={{ background: locked ? "#0b1f3a" : "rgba(11,31,58,0.55)", backdropFilter: "blur(3px)" }}>
            <style>{CSS}</style>
            <div className="qfx-wrap">
                <button className="qfx-x" aria-label="Close"
                    onClick={() => {
                        if (locked) { setPinStage("unlock"); setPinEntry(""); setPinError(""); setUnlockMode("pin"); return; }
                        const dirty = step === "form" || (step === "hub" && !allDone);
                        if (!dirty || window.confirm("Exit? Completed questionnaires are already saved.")) {
                            const saved = Object.values(submitted).some((r) => r.ok);
                            reset(); onClose(saved);
                        }
                    }}>✕</button>

                {/* ── STAFF SELECT ── */}
                {step === "select" && (
                    <div className="qfx-split">
                        <div className="qfx-rail">
                            <div className="qfx-eyebrow"><span className="qfx-eyebrow-mark">BM</span>BrainyMed Care</div>
                            <h1>Health Questionnaires</h1>
                            <p className="qfx-rail-sub">
                                Choose the screenings for {patientName || "this patient"}, then hand
                                the device over. Each form saves the moment it's completed.
                            </p>
                            <div className="qfx-rail-bottom">
                                <div className="qfx-count-big">{selected.length}<span className="dim">selected</span></div>
                                <small className="qfx-count-sub">{selected.length ? `about ${totalMinutes} minutes` : "pick at least one form"}</small>
                            </div>
                        </div>
                        <div className="qfx-panel" ref={panelRef}>
                            <div className="qfx-list-heading">
                                <div>
                                    <h2>Screening forms</h2>
                                    <p>Tap to include a form in this session.</p>
                                </div>
                            </div>
                            {QUESTIONNAIRES.map((q) => {
                                const on = selected.includes(q.formId);
                                return (
                                    <button key={q.formId} type="button" className={`qfx-card ${on ? "" : ""}`}
                                        style={on ? { borderColor: "var(--navy)", boxShadow: "0 8px 24px rgba(11,31,58,.10)" } : undefined}
                                        onClick={() => setSelected((prev) => on ? prev.filter((x) => x !== q.formId) : [...prev, q.formId])}>
                                        <FormBadge formId={q.formId} size={50} />
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            <span className="qfx-card-title" style={{ display: "block" }}>{q.shortName}</span>
                                            <span className="qfx-card-sub" style={{ display: "block" }}>
                                                {q.desc}{q.staffAdministered ? " · staff-administered" : ""}
                                            </span>
                                        </span>
                                        <span className={`qfx-badge ${on ? "done" : "pending"}`}>{on ? "Included" : "Tap to add"}</span>
                                    </button>
                                );
                            })}
                            {selected.includes(6) && (
                                <div className="qfx-qcard" style={{ marginTop: 8 }}>
                                    <div className="qfx-qnum">Home Sleep · Vitals (staff)</div>
                                    <div className="qfx-vitals">
                                        {[["ptheight", "HT"], ["ptweight", "WT"], ["ptbmi", "BMI"], ["ptbp", "BP"], ["ptnc", "NC"]].map(([k, label]) => (
                                            <div key={k}>
                                                <label>{label}</label>
                                                <input value={vitals[k]} onChange={(e) => setVitals((v) => ({ ...v, [k]: e.target.value }))} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {forcePatientView ? (
                                <div className="qfx-privacy" style={{ marginTop: 26 }}>
                                    <span className="qfx-privacy-mark">✓</span>
                                    <span>Runs in Patient View: fullscreen, scores hidden, staff PIN to exit.</span>
                                </div>
                            ) : (
                                <button type="button" className="qfx-card" style={{ marginTop: 18, minHeight: 0, padding: "16px 21px" }}
                                    onClick={() => setPatientView(!patientView)}>
                                    <span style={{
                                        width: 36, height: 20, borderRadius: 999, position: "relative", flexShrink: 0,
                                        background: patientView ? "var(--navy)" : "#cbd3e0", transition: "background .2s",
                                    }}>
                                        <span style={{
                                            position: "absolute", top: 2, left: patientView ? 18 : 2,
                                            width: 16, height: 16, borderRadius: "50%", background: "#fff",
                                            boxShadow: "0 1px 3px rgba(0,0,0,.25)", transition: "left .2s",
                                        }} />
                                    </span>
                                    <span style={{ flex: 1 }}>
                                        <span className="qfx-card-title" style={{ display: "block", fontSize: ".95rem" }}>Patient View — tablet hand-off</span>
                                        <span className="qfx-card-sub" style={{ display: "block" }}>Fullscreen, scores hidden, staff PIN required to exit.</span>
                                    </span>
                                </button>
                            )}
                            <div className="qfx-submit-row">
                                <span className="qfx-note">{selected.length} form{selected.length === 1 ? "" : "s"} selected</span>
                                <button className="qfx-btn" disabled={selected.length === 0}
                                    onClick={() => {
                                        if (effectivePV) { setPinStage("setup"); setPinEntry(""); setPinError(""); }
                                        else setStep("hub");
                                    }}>
                                    {effectivePV ? "Start Patient View →" : "Start →"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── HUB ── */}
                {step === "hub" && (
                    <div className="qfx-split">
                        <div className="qfx-rail">
                            <div className="qfx-eyebrow"><span className="qfx-eyebrow-mark">BM</span>BrainyMed Care</div>
                            <h1>Health Questionnaires</h1>
                            <p className="qfx-rail-sub">
                                Please complete each screening below. Your responses help your care
                                team understand your health needs.
                            </p>
                            <div className="qfx-rail-bottom">
                                <div className="qfx-progress-track"><div className="qfx-progress-fill" style={{ width: `${railProgress}%` }} /></div>
                                <div className="qfx-progress-label">{doneCount} of {activeDefs.length} completed</div>
                            </div>
                        </div>
                        <div className="qfx-panel">
                            <div className="qfx-list-heading">
                                <div>
                                    <h2>Your screening forms</h2>
                                    <p>Tap a form to begin.</p>
                                </div>
                                {!allDone && <span className="qfx-time">About {totalMinutes} minutes</span>}
                            </div>
                            {activeDefs.map((q) => {
                                const isDone = Boolean(submitted[q.formId]);
                                return (
                                    <button key={q.formId} type="button" className={`qfx-card ${isDone ? "done" : ""}`}
                                        disabled={isDone}
                                        onClick={() => {
                                            if (isDone) return;
                                            setActiveFormId(q.formId); setShowValidation(false);
                                            setFormResult(null); setStep("form");
                                        }}>
                                        {isDone
                                            ? <span className="qfx-done-icon">✓</span>
                                            : <FormBadge formId={q.formId} size={50} />}
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            <span className="qfx-card-title" style={{ display: "block" }}>{q.shortName}</span>
                                            <span className="qfx-card-sub" style={{ display: "block" }}>{q.desc}</span>
                                        </span>
                                        <span className={`qfx-badge ${isDone ? "done" : "pending"}`}>{isDone ? "Complete" : "Pending"}</span>
                                        {!isDone && <span className="qfx-arrow">→</span>}
                                    </button>
                                );
                            })}
                            <div className="qfx-privacy">
                                <span className="qfx-privacy-mark">✓</span>
                                <span>Your responses are private and shared only with your care team.</span>
                            </div>
                            {allDone && (
                                <div className="qfx-alldone">
                                    <h2>All questionnaires complete</h2>
                                    <p>
                                        Thank you{patientName ? `, ${patientName.split(" ")[0]}` : ""}. Your care team will
                                        review your responses.{locked ? " Please hand the device back to the staff." : ""}
                                    </p>
                                    <div style={{ marginTop: 20, display: "flex", gap: 12 }}>
                                        {locked ? (
                                            <button className="qfx-btn ghost"
                                                onClick={() => { setPinStage("unlock"); setPinEntry(""); setPinError(""); setUnlockMode("pin"); }}>
                                                Staff Unlock
                                            </button>
                                        ) : (
                                            <button className="qfx-btn" onClick={() => setStep("results")}>View Results →</button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── FORM ── */}
                {step === "form" && def && (
                    <div className="qfx-split">
                        <div className="qfx-rail">
                            <button className="qfx-back-pill"
                                onClick={() => {
                                    if (formResult) { setFormResult(null); setActiveFormId(null); setStep("hub"); return; }
                                    setActiveFormId(null); setStep("hub");
                                }}>‹ All forms</button>
                            <div style={{ marginTop: 56 }}>
                                <h1 style={{ maxWidth: 300 }}>{def.shortName}</h1>
                                <p className="qfx-rail-sub" style={{ color: "var(--gold)", fontSize: ".75rem", letterSpacing: ".09em", textTransform: "uppercase", marginTop: 4 }}>
                                    {def.formName}
                                </p>
                            </div>
                            <div className="qfx-rail-bottom">
                                <div className="qfx-count-big">
                                    {answeredCount}<span className="dim">/</span><span className="dim">{requiredKeys.length}</span>
                                </div>
                                <small className="qfx-count-sub">questions answered</small>
                            </div>
                        </div>
                        <div className="qfx-panel" ref={panelRef}>
                            {def.instructions && <div className="qfx-instructions">{def.instructions}</div>}
                            {def.preamble && <div className="qfx-instructions">{def.preamble}</div>}
                            {(def.sections
                                ? def.sections.map((s) => ({ title: s.title, items: s.keys.map((k) => def.items.find((i) => i.key === k)) }))
                                    .concat([{ title: "More", items: def.items.filter((i) => !def.sections.some((s) => s.keys.includes(i.key))) }])
                                : [{ title: "", items: def.items }]
                            ).map((section, si) => (
                                section.items.filter(Boolean).length > 0 && (
                                    <div key={si}>
                                        {section.title && (
                                            <div className="qfx-qnum" style={{ margin: "22px 4px 12px", color: "var(--gold)", fontSize: ".72rem" }}>{section.title}</div>
                                        )}
                                        {section.items.filter(Boolean).map((item, ii) => {
                                            const answered = formAnswers[item.key] !== undefined && formAnswers[item.key] !== "";
                                            const missing = showValidation && item.required !== false && !answered;
                                            return (
                                                <div key={item.key} className={`qfx-qcard ${answered ? "answered" : ""} ${missing ? "missing" : ""}`}>
                                                    <div className="qfx-qnum">Question {def.items.indexOf(item) + 1}</div>
                                                    <div className="qfx-qtext">{item.text}</div>
                                                    {item.type === "checklist" ? (
                                                        <div className="qfx-opts">
                                                            {item.options.map((opt) => {
                                                                const cur = Array.isArray(formAnswers[item.key]) ? formAnswers[item.key] : [];
                                                                const on = cur.includes(opt);
                                                                return (
                                                                    <button key={opt} type="button" className={`qfx-opt ${on ? "on" : ""}`}
                                                                        onClick={() => setAnswer(item.key, on ? cur.filter((c) => c !== opt) : [...cur, opt])}>
                                                                        {opt}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : item.type === "points" ? (
                                                        <div className="qfx-opts">
                                                            {Array.from({ length: item.max + 1 }, (_, n) => (
                                                                <button key={n} type="button"
                                                                    className={`qfx-opt ${Number(formAnswers[item.key]) === n && formAnswers[item.key] !== undefined ? "on" : ""}`}
                                                                    onClick={() => setAnswer(item.key, n)}>{n}</button>
                                                            ))}
                                                            <span className="qfx-note" style={{ alignSelf: "center" }}>/ {item.max} points</span>
                                                        </div>
                                                    ) : item.type === "scale" ? (
                                                        <div className="qfx-opts">
                                                            {item.options.map((opt) => (
                                                                <button key={opt.value} type="button"
                                                                    className={`qfx-opt ${Number(formAnswers[item.key]) === opt.value && formAnswers[item.key] !== undefined ? "on" : ""}`}
                                                                    onClick={() => setAnswer(item.key, opt.value)}>
                                                                    {opt.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="qfx-opts">
                                                            {item.options.map((opt) => (
                                                                <button key={opt} type="button" className={`qfx-opt ${formAnswers[item.key] === opt ? "on" : ""}`}
                                                                    onClick={() => setAnswer(item.key, opt)}>{opt}</button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )
                            ))}
                            {showValidation && !formComplete && (
                                <div className="qfx-validation">Please answer every question before continuing.</div>
                            )}
                            {formResult ? (
                                (() => {
                                    const tone = toneFor(formResult.scored.interpretation);
                                    return (
                                        <div className="qfx-result" style={{ background: tone.bg, borderColor: tone.border }}>
                                            <h3 style={{ color: tone.color }}>
                                                {formResult.scored.total}/{formResult.scored.max} — {formResult.scored.interpretation}
                                            </h3>
                                            <p style={{ color: tone.color }}>
                                                {formResult.ok ? "Saved to the patient's chart." : "Save FAILED — retry from the Forms tab."}
                                            </p>
                                            <button className="qfx-btn" style={{ marginTop: 20 }}
                                                onClick={() => { setFormResult(null); setActiveFormId(null); setStep("hub"); }}>
                                                ← Back to Questionnaires
                                            </button>
                                        </div>
                                    );
                                })()
                            ) : (
                                <div className="qfx-submit-row">
                                    <span className="qfx-note">All questions required</span>
                                    <button className="qfx-btn" disabled={submitting}
                                        onClick={() => submitForm(def)}>
                                        {submitting ? "Saving…" : "Submit & Continue →"}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── RESULTS (staff only) ── */}
                {step === "results" && (
                    <div className="qfx-split">
                        <div className="qfx-rail">
                            <div className="qfx-eyebrow"><span className="qfx-eyebrow-mark">BM</span>BrainyMed Care</div>
                            <h1>Results</h1>
                            <p className="qfx-rail-sub">{patientName} · {dayjs().format("MMM D, YYYY")}</p>
                            <div className="qfx-rail-bottom">
                                <div className="qfx-count-big">{doneCount}<span className="dim">forms</span></div>
                                <small className="qfx-count-sub">saved to the chart</small>
                            </div>
                        </div>
                        <div className="qfx-panel">
                            {activeDefs.filter((d) => submitted[d.formId]).map((d) => {
                                const r = submitted[d.formId];
                                const tone = toneFor(r.scored.interpretation);
                                return (
                                    <div key={d.formId} className="qfx-qcard" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                                        <FormBadge formId={d.formId} size={46} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div className="qfx-card-title">{d.formName}</div>
                                            <div style={{
                                                display: "inline-block", marginTop: 6, padding: "6px 12px",
                                                borderRadius: 999, fontSize: ".82rem", fontWeight: 600,
                                                background: tone.bg, color: tone.color,
                                            }}>
                                                {r.scored.total}/{r.scored.max} — {r.scored.interpretation}
                                            </div>
                                        </div>
                                        <span className={`qfx-badge ${r.ok ? "done" : "pending"}`}
                                            style={!r.ok ? { background: "#fee2e2", color: "#991b1b" } : undefined}>
                                            {r.ok ? "Saved" : "Failed"}
                                        </span>
                                    </div>
                                );
                            })}
                            <div className="qfx-submit-row">
                                <span className="qfx-note">Scores are stored with each submission.</span>
                                <button className="qfx-btn" onClick={() => { reset(); onClose(true); }}>Done</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── PIN overlay ── */}
            {pinStage && (
                <div className="fixed inset-0 z-[9700] flex items-center justify-center p-4" style={{ background: "rgba(11,31,58,.78)", backdropFilter: "blur(4px)" }}>
                    <div style={{
                        background: "#fff", borderRadius: 24, boxShadow: "0 30px 80px rgba(11,31,58,.35)",
                        width: "100%", maxWidth: 330, padding: "30px 28px", textAlign: "center",
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    }}>
                        <div style={{ fontSize: 17, fontWeight: 700, color: "#0b1f3a" }}>
                            {pinStage === "setup" ? "Set a staff PIN" : "Staff PIN"}
                        </div>
                        <div style={{ fontSize: 12, color: "#7b8798", margin: "5px 0 18px" }}>
                            {pinStage === "setup" ? "4 digits — needed to exit Patient View" : "Enter the PIN to unlock"}
                        </div>
                        {(pinStage === "setup" || unlockMode === "pin") ? (
                            <>
                                <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 6 }}>
                                    {[0, 1, 2, 3].map((i) => (
                                        <span key={i} style={{
                                            height: 15, width: 15, borderRadius: "50%",
                                            background: i < pinEntry.length ? "#0b1f3a" : "#e6e1d8",
                                            transition: "all .15s", transform: i < pinEntry.length ? "scale(1.1)" : "none",
                                        }} />
                                    ))}
                                </div>
                                <div style={{ fontSize: 12, height: 18, color: pinError ? "#d95850" : "transparent" }}>{pinError || "."}</div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginTop: 4 }}>
                                    {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"].map((dgt, i) => (
                                        dgt === "" ? <span key={i} /> :
                                            <button key={i} type="button" onClick={() => keypadPress(dgt)}
                                                style={{
                                                    height: 48, borderRadius: 12, border: "none", cursor: "pointer",
                                                    background: "#f3f0e9", fontSize: 17, fontWeight: 600, color: "#0b1f3a",
                                                }}>
                                                {dgt}
                                            </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div style={{ textAlign: "left" }}>
                                <div style={{ fontSize: 12, height: 18, textAlign: "center", color: pinError ? "#d95850" : "transparent" }}>{pinError || "."}</div>
                                <label style={{ fontSize: 11, fontWeight: 700, color: "#7b8798", display: "block", marginBottom: 4 }}>USERNAME</label>
                                <input autoFocus autoComplete="off" value={credUser}
                                    onChange={(e) => { setPinError(""); setCredUser(e.target.value); }}
                                    style={{ width: "100%", border: "1px solid #e6e1d8", borderRadius: 12, padding: "10px 12px", fontSize: 14, marginBottom: 12 }} />
                                <label style={{ fontSize: 11, fontWeight: 700, color: "#7b8798", display: "block", marginBottom: 4 }}>PASSWORD</label>
                                <input type="password" autoComplete="off" value={credPass}
                                    onChange={(e) => { setPinError(""); setCredPass(e.target.value); }}
                                    onKeyDown={(e) => { if (e.key === "Enter") credUnlock(); }}
                                    style={{ width: "100%", border: "1px solid #e6e1d8", borderRadius: 12, padding: "10px 12px", fontSize: 14 }} />
                            </div>
                        )}
                        {pinStage === "unlock" && (
                            <button type="button"
                                style={{ marginTop: 14, background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#50869A", textDecoration: "underline" }}
                                onClick={() => {
                                    setPinError(""); setPinEntry(""); setCredPass("");
                                    setUnlockMode(unlockMode === "pin" ? "creds" : "pin");
                                }}>
                                {unlockMode === "pin" ? "Forgot PIN? Unlock with username & password" : "Use PIN instead"}
                            </button>
                        )}
                        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 18 }}>
                            {pinStage === "unlock" && (
                                <button type="button" className="qfx-btn ghost" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                                    onClick={() => { setPinStage(null); setPinEntry(""); setPinError(""); setUnlockMode("pin"); setCredPass(""); }}>
                                    Cancel
                                </button>
                            )}
                            {(pinStage === "setup" || unlockMode === "pin") ? (
                                <button type="button" className="qfx-btn" disabled={pinEntry.length !== 4}
                                    style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                                    onClick={confirmPin}>
                                    {pinStage === "setup" ? "Start" : "Unlock"}
                                </button>
                            ) : (
                                <button type="button" className="qfx-btn" disabled={!credUser || !credPass || credBusy}
                                    style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                                    onClick={credUnlock}>
                                    {credBusy ? "Checking…" : "Unlock"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionnaireFill;
